import type { HeaderMotionCatalog, HeaderMotionCatalogEntry, HeaderMotionMedia, HeaderMotionTypeId } from './headerMotion.types';

/** Options the cinematic Motion type uses when the saved config omits or mangles them. */
export const CINEMATIC_DEFAULTS = { filmTexture: true, play: 'first-visit' } as const;

/** Options the bloom Motion type uses when the saved config omits or mangles them. Bloom has no tuning knobs yet. */
export const BLOOM_DEFAULTS = { play: 'first-visit' } as const;

/**
 * Options the wildflowers Motion type uses when the saved config omits or mangles them. `density` is
 * how many stems grow ('meadow' about one per letter, 'few' about 0.4 per letter); `arrangement`
 * reseeds the layout ("New arrangement" in the editor adds 1).
 */
export const WILDFLOWERS_DEFAULTS = { play: 'first-visit', density: 'meadow', arrangement: 0 } as const;

/**
 * Every header Motion type, keyed by its saved `type` id, without any rendering code.
 *
 * Lives in shared so the admin designer can list types without importing the guest registry
 * (which pulls in each type's lazy component). The guest `motionRegistry` spreads these entries,
 * so `supports` and `defaults` have one source of truth. Adding a type means adding an id to
 * `HeaderMotionTypeId`, an entry here, a guest registry entry and an admin label.
 */
export const HEADER_MOTION_CATALOG: Readonly<Record<HeaderMotionTypeId, HeaderMotionCatalogEntry>> = {
  cinematic: {
    id: 'cinematic',
    supports: ['photo', 'video'],
    defaults: CINEMATIC_DEFAULTS,
  },
  bloom: {
    id: 'bloom',
    supports: ['photo', 'video'],
    defaults: BLOOM_DEFAULTS,
  },
  wildflowers: {
    id: 'wildflowers',
    supports: ['photo', 'video'],
    defaults: WILDFLOWERS_DEFAULTS,
  },
};

/**
 * Motion types that can sit on the given header media, in catalog order. Pure.
 * Feeds the admin Header Motion dropdown; `catalog` is widened by tests to register stub types.
 */
export const getMotionTypesFor = (media: HeaderMotionMedia, catalog: HeaderMotionCatalog = HEADER_MOTION_CATALOG): ReadonlyArray<HeaderMotionCatalogEntry> =>
  Object.values(catalog).filter(entry => entry.supports.includes(media));
